import { Alert, Autocomplete, Button, Stack, TextField, Typography } from "@mui/material";
import React, { useCallback } from "react";
import Modal, { CommonModalProps, ModalContext } from "..";
import { cacheService, orderService } from "../../../Core/Api/services";
import { CreateInternalOrderRequest } from "../../../Core/Api/Types/create-internal-order-request";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { InternalOrderInfos } from "../../../Core/Api/Types/internal-order-infos";
import { OrderProductType } from "../../../Core/Api/Types/order-product-type";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import MetadataListTextItem from "../../Misc/Metadata/MetadataListTextItem/metadata-list-text-item";
import ModalConfirmAction, { ModalConfirmActionElementProps } from "../ModalConfirmAction/modal-confirm-action";
import FormSection from "../../Forms/FormSection/form-section";
import { produce } from "immer";
import { useTranslation } from "react-i18next";

export type ModalQualityCheckElementProps = {
  assets: DbAssetObjectInfos[];
};

export type ModalQualityCheckProps = CommonModalProps<ModalQualityCheckElementProps>;

const ModalQualityCheck = React.forwardRef<any, ModalQualityCheckProps>((props, ref) => {
  const { t } = useTranslation();
  const { closeModal, ...modalProps } = props;
  const { assets } = modalProps;

  const modalContext = React.useContext(ModalContext);
  const customSnackbar = useCustomSnackbar();
  const { openModal } = { ...modalContext };

  const [selectedQcTypes, setSelectedQcTypes] = React.useState<{ id: number; value: string }[]>([]);

  const qcType = cacheService.GetMetadata().find((e) => e.tagName === "ingestQCType");

  const handleQcTypeChange = useCallback(
    (objectId: number, value: string | null) => {
      setSelectedQcTypes(
        produce((draft) => {
          let index = draft.findIndex((x) => x.id === objectId);
          if (index === -1 && value) draft.push({ id: objectId, value: value ?? "Default" });
          else if (index !== -1 && value) draft[index] = { id: objectId, value: value ?? "Default" };
          else if (index !== -1 && !value) draft.splice(index, 1);
        })
      );
    },
    [selectedQcTypes]
  );

  const handleConfirmClick = () => {
    if (!openModal) return;

    openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
      displayComment: false,
      commentLabel: t("modal:modalQualityCheck.openModal.label"),
      title: t("modal:modalQualityCheck.openModal.title"),
      onConfirm: handleQCOrder,
    });
  };

  const handleQCOrder = useCallback(() => {
    assets.forEach((asset) => {
      let params: InternalOrderInfos[] = [
        {
          assetId: asset.id,
          fieldTagName: qcType?.tagName!,
          fieldValue: selectedQcTypes.find((x) => x.id === asset.id)?.value ?? null,
        },
      ];

      let request = new CreateInternalOrderRequest({
        assetId: asset.id,
        productType: OrderProductType.QualityCheck,
        parameters: params,
      });

      orderService.CreateInternalOrder(request).then(() => {
        closeModal();
        customSnackbar(t("alert:success.qualityCheckRequestedSuccessfully", { id: asset.id }), "success");
      });
    });
  }, [qcType, selectedQcTypes, t]);

  const applyToAll = (objectId: number) => {
    const value = selectedQcTypes.find((x) => x.id === objectId)?.value || "";
    setSelectedQcTypes(assets.map((x) => ({ id: x.id, value: value })));
  };

  const handleCancelClick = () => {
    closeModal();
  };

  return (
    <Modal
      title={
        assets.length === 1
          ? t("modal:modalQualityCheck.qualityCheckRequestForSingleAsset", { id: assets[0].id })
          : t("modal:modalQualityCheck.qualityCheckRequestForMultipleAssets")
      }
      reference={ref}
      closeModal={handleCancelClick}
      width={700}
    >
      <Stack spacing={2}>
        <Typography variant="h6" component="span" color="text.secondary">
          {t("modal:modalQualityCheck.subTitle")}
        </Typography>
        {assets.map((asset, index) => (
          <FormSection title={asset.name} key={asset.id} size="small" defaultExpanded={true} sx={{ mb: 1 }}>
            <MetadataListTextItem title={t("common:infos")}>
              {t("modal:modalQualityCheck.assetOfType", { id: asset.id, type: asset.type })}
            </MetadataListTextItem>
            <MetadataListTextItem title={t("modal:modalQualityCheck.body.qcType")}>
              <Stack display={"flex"} flexDirection={"row"}>
                <Button
                  disabled={
                    !selectedQcTypes.find((y) => y.id === asset.id)?.value ||
                    (selectedQcTypes.map((x) => x.value).filter((value, index, self) => self.indexOf(value) === index)
                      .length < 2 &&
                      selectedQcTypes.length === assets.length)
                  }
                  variant="outlined"
                  color="primary"
                  sx={{ mt: 1, mr: 2 }}
                  onClick={() => applyToAll(asset.id)}
                >
                  {t("button:applyToAll")}
                </Button>
                <Autocomplete
                  onChange={(e, value) => handleQcTypeChange(asset.id, value)}
                  options={qcType?.values!.map((x) => x.value) || []}
                  value={selectedQcTypes.find((y) => y.id === asset.id)?.value || ""}
                  renderInput={(params) => (
                    <TextField
                      style={{ width: "300px" }}
                      {...params}
                      placeholder={t("field:placeholder.selectQcType")}
                    />
                  )}
                  sx={{ mt: -2 }}
                />
              </Stack>
            </MetadataListTextItem>
          </FormSection>
        ))}

        <Alert severity="info">{t("modal:modalQualityCheck.body.chargedAlert")}</Alert>

        <Stack direction="row" justifyContent="space-between" mt={4}>
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button
            onClick={handleConfirmClick}
            variant="contained"
            color="primary"
            disabled={selectedQcTypes.length !== assets.length}
          >
            {t("common:request")}
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
});

export default ModalQualityCheck;
